<template>
  <Modal name="request-quote" :title="submitted ? $t('Quote Requested') : $t('Request a Quote')"
    class="fixed-top checkout-modal" @before-open="onBeforeOpen" @opened="onOpened">
    <!-- HubSpot Form -->
    <div v-if="!submitted" class="product-section">
      <div class="modal-heading">
        <h2>{{ $t('Get a Quote') }}</h2>
        <p>{{ $t('Fill in the form below and our sales team will get back to you shortly.') }}</p>
      </div>

      <Loader :loading="formLoading" :title="$t('Loading form...')" full />
      <Loader :loading="submitting" full />
      <div id="hs-form-container" ref="hsFormContainer"></div>
    </div>

    <!-- Success State -->
    <div v-else class="quote-success">
      <div class="quote-success__hero">
        <div class="quote-success__icon-wrapper">
          <Icon name="check" family="fas" class="quote-success__icon" />
        </div>
      </div>

      <div class="quote-success__content">
        <h3>{{ $t('Your quote request has been submitted!') }}</h3>
        <p>
          {{ $t('A member of our sales team will review your request and be in touch with you shortly.') }}
        </p>
      </div>
    </div>

    <template #footer>
      <div class="row">
        <div class="col">
          <a class="support-block support-block--minified" @click="onContactUs">
            <div class="content">
              <h5>{{ $t('Need help?') }}</h5>
              <p>{{ $t('Contact us') }}</p>
            </div>
          </a>
        </div>
        <div class="col-auto">
          <button v-if="submitted" class="btn btn-outline-primary px-5" @click="onClose">
            {{ $t('Close') }}
          </button>
          <button v-else class="btn btn-primary px-5" :disabled="submitting" @click="onSubmit">
            {{ $t('Submit') }}
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script>
import Modal from '@/components/Modal.vue';
import Icon from 'common/components/Icon.vue';
import Loader from 'common/components/Loader.vue';

const HS_PORTAL_ID = '45533112';
const HS_FORM_ID = 'd67473ca-b78a-4a78-b456-c29daaee6d4c';

// Maps licensecount values to HubSpot "Number of Users" dropdown options
const LICENSE_TO_HS_USERS = {
  '0-10': '<10',
  '11-50': '10-50',
  '51-100': '51-100',
  '101-500': '101-1000',
  '501-1000': '101-1000',
  '1001-5000': '1001+',
  '5001-10000': '1001+',
  '10001+': '1001+',
};

const PREFILL_MAP = {
  firstname: (u) => (u.customername || '').trim().split(/\s+/)[0] || '',
  lastname: (u) => (u.customername || '').trim().split(/\s+/).slice(1).join(' ') || '',
  email: (u) => u.email || '',
  phone: (u) => u.phone || '',
  countrylist: (u) => u.country || '',
  company: (u) => u.company || '',
  number_of_users__c: (u) => LICENSE_TO_HS_USERS[u.licensecount] || '',
};

function setNativeValue(el, value) {
  if (el.tagName === 'SELECT') {
    el.value = value;
    el.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }

  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  if (setter) {
    setter.call(el, value);
  } else {
    el.value = value;
  }
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

export default {
  name: 'RequestQuote',
  components: {
    Modal,
    Icon,
    Loader,
  },
  data() {
    return {
      submitted: false,
      formLoading: true,
      submitting: false,
    };
  },
  computed: {
    user() {
      return this.$store.state.core.user;
    },
  },
  methods: {
    onBeforeOpen() {
      this.submitted = false;
      this.formLoading = true;
    },
    async onOpened() {
      try {
        await this.loadHubSpotScript();
        await this.createHubSpotForm();
      } catch {
        // silently fail
      } finally {
        this.formLoading = false;
      }
    },
    loadHubSpotScript() {
      return new Promise((resolve, reject) => {
        if (window.hbspt) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = '//js.hsforms.net/forms/embed/v2.js';
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    createHubSpotForm() {
      return new Promise((resolve, reject) => {
        if (!window.hbspt) {
          reject(new Error('HubSpot SDK not loaded'));
          return;
        }

        const container = this.$refs.hsFormContainer;
        if (!container) {
          reject(new Error('HubSpot form container not found'));
          return;
        }

        container.innerHTML = '';

        window.hbspt.forms.create({
          portalId: HS_PORTAL_ID,
          formId: HS_FORM_ID,
          target: '#hs-form-container',
          css: '',
          onFormReady: () => {
            this.prefillForm();
            resolve();
          },
          onFormSubmit: () => {
            this.submitting = true;
          },
          onFormSubmitted: () => {
            this.submitting = false;
            this.submitted = true;
          },
        });
      });
    },
    prefillForm() {
      const container = this.$refs.hsFormContainer;
      if (!container) return;

      for (const [hsName, getter] of Object.entries(PREFILL_MAP)) {
        const value = getter(this.user);
        if (!value) continue;

        const el = container.querySelector(`[name="${hsName}"]`);
        if (el) {
          setNativeValue(el, value);
        }
      }
    },
    onSubmit() {
      const container = this.$refs.hsFormContainer;
      if (!container) return;
      const btn = container.querySelector('.hs-button');
      if (btn) btn.click();
    },
    onContactUs() {
      this.$modal.hide('request-quote');
      this.$modal.show('contact-us');
    },
    onClose() {
      this.$modal.hide('request-quote');
    },
  },
};
</script>

<style lang="scss">
#hs-form-container {
  .hs-form {
    display: flex;
    flex-wrap: wrap;
    gap: 0 1rem;

    >* {
      flex: 0 0 calc(50% - 0.5rem);
    }

    // Full-width elements
    .hs_description,
    .legal-consent-container,
    .hs-submit {
      flex: 0 0 100%;
    }

    .hs_recaptcha,
    .hs_error_rollup {
      display: none;
    }

    .hs-form-field {
      margin-bottom: 1rem;

      label:not(.hs-form-checkbox-display) {
        display: block;
        margin: 0 0 8px;
        font-size: 14px;
        font-weight: 600;
        color: #4E5D78;
      }

      .hs-input:not([type="checkbox"]) {
        width: 100%;
        height: 35.59px;
        padding: 0.5rem 0.75rem;
        font-size: 0.875rem;
        line-height: 1.5;
        color: var(--text-dark, #333);
        background-color: #fff;
        border: 1px solid var(--border-color-light, #ddd);
        border-radius: 4px;
        transition: border-color 0.2s ease, box-shadow 0.2s ease;

        &:focus {
          outline: none;
          border-color: var(--color-primary, #0d6efd);
          box-shadow: 0 0 0 0.2rem var(--color-primary--shadow, rgba(13, 110, 253, 0.25));
        }

        &::placeholder {
          color: var(--text-muted, #999);
        }
      }

      select.hs-input {
        appearance: auto;
      }

      textarea.hs-input {
        height: auto;
        min-height: 80px;
        resize: vertical;
      }
    }

    // Error state
    .hs-form-field:has(.hs-error-msgs) {
      label:not(.hs-form-checkbox-display) {
        color: var(--color-danger, #dc3545) !important;
      }

      .hs-input:not([type="checkbox"]) {
        border-color: var(--color-danger, #dc3545) !important;
      }
    }

    .hs-error-msgs {
      list-style: none;
      padding: 0;
      margin: 0.25rem 0 0;

      li label {
        font-size: 0.8rem !important;
        font-weight: 400 !important;
        color: var(--color-danger, #dc3545) !important;
        margin-bottom: 0 !important;
      }
    }

    // Legal consent / checkboxes
    .legal-consent-container {
      margin-bottom: 1rem;

      ul {
        list-style: none;
        padding: 0;
        margin: 0;
      }

      .hs-form-booleancheckbox-display {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;

        input[type="checkbox"] {
          width: auto !important;
          margin-top: 0.25rem;
          flex-shrink: 0;
        }

        span {
          font-size: 0.85rem;
          color: var(--text-medium, #666);

          a {
            color: var(--color-primary, #0d6efd);
            text-decoration: underline;
          }
        }
      }

      .hs-form-booleancheckbox {
        margin-bottom: 0.5rem;
      }

      .hs-richtext {
        font-size: 0.85rem;
        color: var(--text-medium, #666);
        margin-bottom: 0.5rem;

        a {
          color: var(--color-primary, #0d6efd);
          text-decoration: underline;
        }
      }
    }

    .hs-submit {
      display: none;
    }

  }

}

// HubSpot submitted message (hidden — we show our own)
.submitted-message {
  display: none !important;
}
</style>

<style lang="scss" scoped>
.quote-success {
  margin-inline: -2rem;
  margin-top: calc(-2rem - 1px);

  &__hero {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 120px;
    background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
  }

  &__icon-wrapper {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: var(--color-success, #92cc35);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__icon {
    font-size: 1.4rem;
    color: #fff;
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 2rem 1rem 1rem;

    h3,
    p {
      width: 380px;
      max-width: 100%;
      text-align: center;
      margin: 0;
    }

    h3 {
      color: var(--text-dark);
      font-size: 16px;
      font-weight: 500;
    }

    p {
      color: var(--text-light);
      font-size: 0.9rem;
      line-height: 1.5;
    }
  }
}
</style>
